import React, { useCallback, useEffect, useState } from 'react'

import { ButtonType } from 'src/apollo/sites'
import Button, { BrandedButton } from 'src/shared/components/common/button'
import { formatAddress } from 'src/shared/components/common/form_input/LocationInput'
import { ToggleInput } from 'src/shared/components/common/forms'
import {
  ModalCloseButton,
  ModalContent
} from 'src/shared/components/common/modal/Modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useLocationSorting } from 'shared/js/hooks/useLocationSorting'
import { t } from 'src/translations'
import { LocationOptionLabel } from './LocationOptionLabel'

type GateStep = 'confirm' | 'change'

type Props = {
  onConfirmCurrentLocation: () => void
  onConfirmLocationChange: (locationGuid: string) => void
  /**
   * Ask the browser for the guest's coordinates when they reach the picker step so
   * locations can be sorted by (and labelled with) distance.
   * Off in the editor preview
   */
  requestUserLocationOnChangeStep?: boolean
  disableFocusTrap?: boolean
  // Renders a close button in the header for editor preview
  onClose?: () => void
}

/**
 * The two-step body of the location selection gate, shared by the live gate and
 * the editor preview. Owns only presentational state (which step is showing, which
 * radio is selected); everything with a side effect is passed in by the caller.
 */
export const LocationSelectionGateContent = ({
  onConfirmCurrentLocation,
  onConfirmLocationChange,
  requestUserLocationOnChangeStep = false,
  disableFocusTrap,
  onClose
}: Props) => {
  const { locations, selectedLocation, restaurant, ooRestaurant } =
    useRestaurant()
  const [step, setStep] = useState<GateStep>('confirm')
  const [draftSelectedLocation, setDraftSelectedLocation] = useState<
    string | null
  >(null)
  const { sortedLocations, requestUserLocation, userLocation } =
    useLocationSorting(locations)

  useEffect(() => {
    if (step === 'change' && requestUserLocationOnChangeStep) {
      requestUserLocation()
    }
  }, [step, requestUserLocation, requestUserLocationOnChangeStep])

  const onConfirmChange = useCallback(() => {
    if (draftSelectedLocation) {
      onConfirmLocationChange(draftSelectedLocation)
    }
  }, [draftSelectedLocation, onConfirmLocationChange])

  return step === 'confirm' ? (
    <ModalContent
      contentClassName='modalContentV2 bottomDrawerMobile fulfillmentModal changeLocationModal confirmLocationGate'
      ariaLabelledBy='location-selection-gate-heading'
      disableFocusTrap={disableFocusTrap}
    >
      <div className='modalHeader'>
        <h2 id='location-selection-gate-heading'>
          {t('location-selection-gate.confirm-header')}
        </h2>
        {onClose && <ModalCloseButton onClose={onClose} />}
      </div>

      <div className='modalContent'>
        <div className='currentLocation'>
          <div className='locationDetails'>
            <div className='address'>
              <span className='addressLine1'>
                {restaurant?.name}
                {selectedLocation.name ? ` | ${selectedLocation.name}` : ''}
              </span>
              <span className='addressCity'>
                {formatAddress(selectedLocation)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className='footer'>
        <BrandedButton
          variant={ButtonType.Primary}
          onClick={onConfirmCurrentLocation}
        >
          {t('location-selection-gate.yes-continue')}
        </BrandedButton>
        <BrandedButton
          variant={ButtonType.Inverted}
          onClick={() => setStep('change')}
          className='cancelButton'
        >
          {t('location-selection-gate.no-change-location')}
        </BrandedButton>
      </div>
    </ModalContent>
  ) : (
    <ModalContent
      contentClassName='modalContentV2 bottomDrawerMobile fulfillmentModal changeLocationModal'
      ariaLabelledBy='location-selection-gate-heading'
      disableFocusTrap={disableFocusTrap}
    >
      <div className='modalHeader'>
        <h2 id='location-selection-gate-heading'>
          {t('location-selection-gate.header')}
        </h2>
        {onClose && <ModalCloseButton onClose={onClose} />}
      </div>

      <div className='modalContent'>
        <div className='otherLocations'>
          <ul className='radioButtons'>
            {sortedLocations.map((location) => (
              <li key={location.externalId} className='radioButtonOption'>
                <ToggleInput
                  checked={draftSelectedLocation === location.externalId}
                  onChange={() => setDraftSelectedLocation(location.externalId)}
                  name={location.name ?? location.externalId}
                  id={location.externalId}
                  type='radio'
                  dataTestId={`input-location-${location.name}`}
                >
                  <LocationOptionLabel
                    location={location}
                    showDistance={!!userLocation}
                    locale={ooRestaurant?.i18n?.locale}
                  />
                </ToggleInput>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className='footer'>
        <Button
          variant={ButtonType.Primary}
          onClick={onConfirmChange}
          disabled={!draftSelectedLocation}
        >
          {t('change-location-modal.button.continue')}
        </Button>
      </div>
    </ModalContent>
  )
}
